import {
    createMetaDescription,
    createMetaTitle,
    createOGTags,
    getCanonicalHreflang,
    mergeMeta,
} from '@modules/seo';
import type { MetaDescriptor } from 'react-router';
import type { ProjectImageLabelView } from '../config/project-browse-views';
import type { createImageLabelBrowseLoader } from './image-label-browse-loader.server';

type ImageLabelBrowseLoader = ReturnType<typeof createImageLabelBrowseLoader>;

/**
 * SEO tags for a Spaces / Styles browse.
 *
 * These pages are a wall of labelled photography, so the body text a crawler
 * sees is each image's alt — the AI's own description of the photo. The title
 * and description name the axis being browsed, and the active pill within it,
 * so the page reads as its own destination rather than a near-duplicate of
 * /projects.
 */
export const createImageLabelBrowseMeta = (view: ProjectImageLabelView) =>
    mergeMeta<ImageLabelBrowseLoader>(
        () => [],
        (props) => {
            const { data } = props;
            const noun = view === 'styles' ? 'style' : 'space';
            const label = data?.selectedLabel ?? data?.rootLabel;

            const title = createMetaTitle(
                props,
                [
                    data?.selectedLabel
                        ? `${data.selectedLabel} project photos`
                        : `Browse project photos by ${noun}`,
                ],
                true,
                data?.page,
            );

            const description = createMetaDescription([
                `Browse project photography by ${noun}${
                    label ? `, starting with ${label}` : ''
                }. Every photo is labelled from the image itself, so you can find the rooms, styles and finishes you want and go straight to the professionals behind them.`,
            ]);

            const metaDescriptors: MetaDescriptor[] = [
                { title },
                { name: 'description', content: description },
                ...createOGTags({ meta: props, title, description }),
                ...getCanonicalHreflang(props),
            ];

            return metaDescriptors;
        },
    );
